import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { decodeId, encodeId } from "utils";
import {
  getPolicyById,
  reNotifyPolicy,
  policyAssignmentAction,
  updatePolicyNotification,
  usePolicyAssignments,
  deletePolicyFolder,
} from "repositories/office-policy-management-repository";
import constants, {
  officePolicyStatusOptions,
  prioritiesOptions,
} from "constants";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";

const POLICY_QUERY_KEY = ["policy", "view"];

function getStatusLabel(statusId) {
  if (statusId == null) return "";
  const option = (officePolicyStatusOptions || []).find(
    (o) => Number(o.value) === Number(statusId),
  );
  return option?.label ?? "";
}

function getPriorityLabel(priorityId) {
  if (priorityId == null) return "";
  const option = (prioritiesOptions || []).find(
    (o) => Number(o.id) === Number(priorityId),
  );
  return option?.name ?? "";
}

/**
 * Policy-level status label: when isDraft is true show "Draft", otherwise use status options.
 * @param {{ overallStatus?: number; isDraft?: boolean } | null} policy
 * @returns {string}
 */
function getPolicyStatusLabel(policy) {
  if (policy?.isDraft) return "Draft";
  return getStatusLabel(policy?.overallStatus);
}

/**
 * @param {string} path
 * @returns {string} filename from path/URL
 */
function getFilenameFromPath(path) {
  if (!path || typeof path !== "string") return "";
  const trimmed = path.trim();
  if (!trimmed) return "";
  const lastSlash = trimmed.lastIndexOf("/");
  return lastSlash >= 0 ? trimmed.slice(lastSlash + 1) : trimmed;
}

/**
 * Hook for ViewPolicy: loads policy by id from URL, derives display values, filter state, and actions.
 */
export function useViewPolicy() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { officePolicyFolderId: folderIdFromPath, officePolicyId } = useParams();
  const {
    effectiveOwnerId,
    effectiveOwnerIdString,
    getIdToCheckOwnerChange,
    profile,
  } = useAccountOwnerCommon();
  const location = useLocation();
  const policyIdFromQuery =
    location.search && typeof URLSearchParams !== "undefined"
      ? new URLSearchParams(location.search).get("policyId")
      : null;
  const policyId =
    decodeId(policyIdFromQuery) || decodeId(officePolicyId) || null;

  const [assignmentsPage, setAssignmentsPage] = useState(1);
  const [accumulatedItems, setAccumulatedItems] = useState([]);
  const isFirstFilterMount = useRef(true);

  const [searchName, setSearchName] = useState("");
  const [selectedOffice, setSelectedOffice] = useState({
    value: "",
    label: "All Offices",
  });
  const [selectedRole, setSelectedRole] = useState({
    value: "",
    label: "All Roles",
  });
  const [selectedStatus, setSelectedStatus] = useState({
    value: "",
    label: "All Status",
  });
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const {
    data: policy,
    isLoading: policyLoading,
    error: policyError,
    refetch,
  } = useQuery([POLICY_QUERY_KEY, policyId], () => getPolicyById(policyId), {
    enabled: !!policyId,
  });
  useEffect(() => {
    if (getIdToCheckOwnerChange()) {
      history.push(
        constants.routes.officePolicyManagements.officePolicyManagement,
      );
    }
  }, [effectiveOwnerId]);

  const searchTermEffective =
    (searchName?.trim()?.length ?? 0) >= 3
      ? searchName?.trim() || undefined
      : undefined;

  const assignmentsFilters = useMemo(
    () => ({
      searchTerm: searchTermEffective,
      officeId: selectedOffice?.value || undefined,
      roleId: selectedRole?.value || undefined,
      status:
        selectedStatus?.value !== undefined && selectedStatus?.value !== ""
          ? selectedStatus.value
          : undefined,
    }),
    [
      searchTermEffective,
      selectedOffice?.value,
      selectedRole?.value,
      selectedStatus?.value,
    ],
  );

  const ASSIGNMENTS_PAGE_SIZE = 10;

  const {
    data: assignmentsData,
    isLoading: assignmentsLoading,
    isFetching: assignmentsFetching,
    refetch: refetchAssignments,
  } = usePolicyAssignments(
    policyId,
    assignmentsPage,
    ASSIGNMENTS_PAGE_SIZE,
    assignmentsFilters,
    { enabled: !!policyId },
  );

  useEffect(() => {
    if (isFirstFilterMount.current) {
      isFirstFilterMount.current = false;
      return;
    }
    setAssignmentsPage(1);
    setAccumulatedItems([]);
  }, [
    searchTermEffective,
    selectedOffice?.value,
    selectedStatus?.value,
    selectedRole?.value,
  ]);

  useEffect(() => {
    const items = assignmentsData?.items ?? [];
    if (assignmentsPage === 1 && items.length === 0) {
      setAccumulatedItems([]);
      return;
    }
    if (items.length === 0) return;
    if (assignmentsPage === 1) {
      setAccumulatedItems(items);
    } else {
      setAccumulatedItems((prev) => [...prev, ...items]);
    }
  }, [
    assignmentsData?.items,
    assignmentsPage,
    searchTermEffective,
    selectedOffice?.value,
    selectedRole?.value,
    selectedStatus?.value,
  ]);

  /** Offices from detail API: policyFolder.offices (design: "This policy applies to: ...") */
  const officesLabel = useMemo(() => {
    const fromFolder =
      policy?.policyFolder?.offices ?? policy?.policyFolder?.Offices ?? [];
    const offices = Array.isArray(fromFolder)
      ? fromFolder
      : (policy?.offices ?? policy?.officeIds ?? []);
    if (!Array.isArray(offices) || offices.length === 0) return "";
    return offices
      .map((o) => o.officeName ?? o.name ?? "")
      .filter(Boolean)
      .join(", ");
  }, [policy]);

  const assignmentsPagination = assignmentsData?.pagination ?? {};
  const totalAssignees =
    assignmentsPagination?.totalItems ??
    assignmentsPagination?.totalCount ??
    policy?.totalAssignees ??
    0;

  const oneAssigneeDisplayName = useMemo(() => {
    if (totalAssignees !== 1 || !policy?.oneAssignee) return "";
    const o = policy.oneAssignee;
    const first = o.firstName ?? o.staffFirstName ?? "";
    const last = o.lastName ?? o.staffLastName ?? "";
    return [first, last].filter(Boolean).join(" ") || "";
  }, [policy, totalAssignees]);

  const assigneesList = useMemo(() => {
    const list = Array.isArray(accumulatedItems) ? accumulatedItems : [];
    return list.map((a) => ({
      id: a.id ?? a.staffMemberId ?? a.staffId ?? a.user?.id,
      assignmentId: a.id ?? a.assignmentId ?? a.policyAssignmentId,
      status: a.status,
      statusLabel: getStatusLabel(a.status),
      name:
        [
          a.firstName ??
            a.staffFirstName ??
            a.user?.firstName ??
            a.user?.fullName,
          a.lastName ?? a.staffLastName ?? a.user?.lastName,
        ]
          .filter(Boolean)
          .join(" ")
          .trim() ||
        (a.displayName ?? a.name ?? ""),
      totalComments: a.totalComments ?? a.commentCount ?? 0,
      userId: a.user?.id ?? a.userId,
      officeId: a.officeId ?? a.office?.id,
      officeName: a.officeName ?? a.office?.name ?? "",
      ownerId: a.ownerId,
      roleId: a.roleId ?? a.designationId ?? a.designation?.id,
      rejectionReason: a.rejectionReason ?? a.reason,
      isReassigned: a.isReassigned,
      dueDate: a.dueDate,
      isOverdue: a.isOverdue,
    }));
  }, [accumulatedItems, policy?.assignees]);

  /** Office options from policy folder (detail API: policyFolder.offices), same as "This policy applies to" banner. */
  const officeOptions = useMemo(() => {
    const fromFolder =
      policy?.policyFolder?.offices ?? policy?.policyFolder?.Offices ?? [];
    const offices = Array.isArray(fromFolder)
      ? fromFolder
      : (policy?.offices ?? policy?.officeIds ?? []);
    if (!Array.isArray(offices) || offices.length === 0) {
      return [{ value: "", label: "All Offices" }];
    }
    const options = offices
      .filter((o) => (o?.officeId ?? o?.id) != null)
      .map((o) => ({
        value: o.officeId ?? o.id,
        label: o.officeName ?? o.name ?? `Office ${o.officeId ?? o.id}`,
      }));
    return [{ value: "", label: "All Offices" }, ...options];
  }, [policy]);
  const updatedStatusRef = useRef([]);

  /** Status options from assignees (backup: extractFromAssigneesList uniqueSatus) */
  const statusOptions = useMemo(() => {
    if (updatedStatusRef.current.length > 1) return updatedStatusRef.current;
    const seen = new Set();
    const options = [];
    assigneesList.forEach((a) => {
      const status = a.status;
      if (status != null && !seen.has(status)) {
        seen.add(status);
        options.push({
          value: status,
          label: getStatusLabel(status),
        });
      }
    });
    updatedStatusRef.current = [{ value: "", label: "All Status" }, ...options];
    return updatedStatusRef.current;
  }, [assigneesList]);

  const filteredAssignees = useMemo(() => {
    let list = assigneesList;
    const search = (searchName || "").trim().toLowerCase();
    // Server search applies at 3+ chars; below that show the full loaded list.
    if (search.length >= 3) {
      list = list.filter((a) => a.name.toLowerCase().includes(search));
    }
    return list;
  }, [assigneesList, searchName]);

  const folderIdFromPolicy =
    policy?.policyFolderId ??
    policy?.PolicyFolderId ??
    policy?.policyFolder?.id ??
    policy?.policyFolder?.policyFolderId;
  const officePolicyFolderId =
    folderIdFromPath ||
    (folderIdFromPolicy != null && folderIdFromPolicy !== ""
      ? encodeId(folderIdFromPolicy)
      : undefined);

  const onBack = useCallback(() => {
    if (window.history.length > 1) {
      history.goBack();
      return;
    }
    if (!officePolicyFolderId) {
      history.push({
        pathname:
          constants.routes.officePolicyManagements.officePolicyManagement,
        search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
      });
      return;
    }
    const listingPath =
      constants.routes.officePolicyManagements.policyDocumentListing;
    const path = listingPath.replace(
      ":officePolicyFolderId",
      officePolicyFolderId,
    );
    history.push({
      pathname: path,
      search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
    });
  }, [history, officePolicyFolderId, effectiveOwnerIdString]);

  const handleEdit = useCallback(() => {
    if (!policyId || !officePolicyFolderId) return;
    const route =
      constants.routes.officePolicyManagements.officePolicyManagementEditPolicy;
    const path = route
      .replace(":officePolicyFolderId", officePolicyFolderId)
      .replace(":policyId", encodeId(policyId));
    history.push({
      pathname: path,
      search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
    });
  }, [history, policyId, officePolicyFolderId, effectiveOwnerIdString]);

  const handleViewLogs = useCallback(() => {
    if (!policyId) return;
    const route =
      constants.routes.officePolicyManagements.policyDetailsHistoryLogs;
    history.push({
      pathname: `${route.replace(":policyId", encodeId(policyId))}`,
      search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
    });
  }, [history, policyId, effectiveOwnerIdString]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!policyId) return;
    try {
      await deletePolicyFolder({
        OwnerId: effectiveOwnerId,
        Items: [
          {
            PolicyFolderId: decodeId(officePolicyFolderId),
            PolicyId: policyId,
          },
        ],
      });
      setDeleteModalOpen(false);
      toast.success("Policy deleted.");
      queryClient.invalidateQueries([POLICY_QUERY_KEY]);
      onBack();
    } catch (e) {
      toast.error(e?.message ?? "Delete failed.");
    }
  }, [policyId, queryClient, onBack, officePolicyFolderId, effectiveOwnerId]);

  const handleReNotify = useCallback(async () => {
    if (!policyId) return;
    setActiveMenuId(null);

    try {
      await reNotifyPolicy({ policyIds: [policyId] });
      toast.success("Re-notify sent.");
      queryClient.invalidateQueries([POLICY_QUERY_KEY, policyId]);
      refetch();
      refetchAssignments?.();
    } catch (e) {
      toast.error(e?.message ?? "Re-notify failed.");
    }
  }, [policyId, refetch, refetchAssignments, queryClient]);

  const handleAssignmentAction = useCallback(
    async (assignmentId, action, params) => {
      setActiveMenuId(null);
      try {
        await policyAssignmentAction(assignmentId, action, params);
        toast.success("Action completed.");
        queryClient.invalidateQueries([POLICY_QUERY_KEY, policyId]);
        refetch();
        refetchAssignments?.();
      } catch (e) {
        toast.error(e?.message ?? "Action failed.");
      }
    },
    [policyId, refetch, refetchAssignments, queryClient],
  );

  const handleReassign = useCallback(
    async (assignee) => {
      const assignmentId = assignee?.assignmentId ?? assignee?.id;
      const userId = assignee?.userId ?? assignee?.user?.id;
      const officeId = assignee?.officeId;
      if (!assignmentId || userId == null || officeId == null) return;

      setActiveMenuId(null);
      const loggedInUserId = profile?.id;

      try {
        await policyAssignmentAction(assignmentId, "Reassign", {
          assignees: [
            {
              UserId: Number(userId),
              OfficeId: Number(officeId),
              IsOwner: Number(loggedInUserId) === Number(userId),
            },
          ],
          accountOwnerId: effectiveOwnerId ?? undefined,
        });
        toast.success("Action completed.");
        queryClient.invalidateQueries([POLICY_QUERY_KEY, policyId]);
        refetch();
        refetchAssignments?.();
      } catch (e) {
        toast.error(e?.message ?? "Action failed.");
      }
    },
    [
      profile?.id,
      effectiveOwnerId,
      policyId,
      refetch,
      refetchAssignments,
      queryClient,
    ],
  );

  const goToComments = useCallback(
    (assignmentId) => {
      if (!assignmentId) return;
      const route =
        constants.routes.officePolicyManagements.officePolicyManagementDetails;
      history.push({
        pathname: route.replace(":officePolicyId", encodeId(assignmentId)),
        search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
      });
    },
    [history, effectiveOwnerIdString],
  );

  const handleToggleChange = useCallback(
    async (field, value) => {
      if (!policy || !policyId) return;
      try {
        const payload = {
          notifyAssignees: !!(policy.notifyAssignees ?? policy.NotifyAssignees),
          isNotifyManagement: !!(
            policy.isNotifyManagement ??
            policy.notifyAssignor ??
            policy.NotifyAssignor
          ),
          RemindAfterPublish: !!(
            (policy.remindAfterPublish ?? policy.RemindAfterPublish ?? 0) > 0
          ),
          RemindBeforeDueDate: !!(
            (policy.remindBeforeDueDate ?? policy.RemindBeforeDueDate ?? 0) > 0
          ),
        };
        if (field === "notifyAssignees") payload.notifyAssignees = !!value;
        if (field === "isNotifyManagement")
          payload.isNotifyManagement = !!value;
        if (field === "remindAfterPublish")
          payload.RemindAfterPublish = !!value;
        if (field === "remindBeforeDueDate")
          payload.RemindBeforeDueDate = !!value;
        await updatePolicyNotification(policyId, payload);
        toast.success("Policy updated.");
        queryClient.invalidateQueries([POLICY_QUERY_KEY, policyId]);
        refetch();
        refetchAssignments?.();
      } catch (e) {
        toast.error(e?.message ?? "Update failed.");
      }
    },
    [policy, policyId, refetch, refetchAssignments, queryClient],
  );

  const isLoading = policyLoading;

  const assigneesSectionLoading =
    (assignmentsLoading || assignmentsFetching) &&
    accumulatedItems.length === 0;

  const hasNextAssigneesPage = (totalAssignees ?? 0) > accumulatedItems.length;
  const isFetchingNextAssigneesPage =
    !!assignmentsFetching && assignmentsPage > 1;
  const loadMoreAssignees = useCallback(() => {
    if (!hasNextAssigneesPage || isFetchingNextAssigneesPage) return;
    setAssignmentsPage((p) => p + 1);
  }, [hasNextAssigneesPage, isFetchingNextAssigneesPage]);

  return {
    policy,
    policyId,
    policyError,
    assignmentsLoading: assigneesSectionLoading,
    officePolicyFolderId,
    isLoading,
    assigneesSectionLoading,
    refetch,
    refetchAssignments,
    officesLabel,
    totalAssignees,
    oneAssigneeDisplayName,
    assigneesList,
    filteredAssignees,
    officeOptions,
    statusOptions,
    searchName,
    setSearchName,
    selectedOffice,
    setSelectedOffice,
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    activeMenuId,
    setActiveMenuId,
    deleteModalOpen,
    setDeleteModalOpen,
    onBack,
    handleEdit,
    handleViewLogs,
    handleDeleteConfirm,
    handleReNotify,
    handleAssignmentAction,
    handleReassign,
    goToComments,
    handleToggleChange,
    getStatusLabel,
    getPriorityLabel,
    getPolicyStatusLabel,
    getFilenameFromPath,
    hasNextAssigneesPage,
    loadMoreAssignees,
    isFetchingNextAssigneesPage,
  };
}
